import type { CityConfig } from "@/pages/city-template/types";

export const eugeneConfig: CityConfig = {
  city: "Eugene",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "eugene",
  route: "/cities/eugene",
  title: "Emergency Passport Eugene, OR | Expedited Passport Renewal & Rush Services",
  metaDescription: "Emergency passport and expedited passport services in Eugene, OR — as fast as 24 hours. Fast passport renewal, child passport expediting, and same-day help for Eugene, Springfield, and Lane County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Eugene, Oregon",
  heroSubtitle: "Passport expired? Trip coming up fast? Fast Passport Center helps Eugene and Lane County travelers get their passports quickly — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Eugene%20Oregon%20skyline%20with%20Willamette%20River%20in%20foreground%2C%20lush%20green%20Cascade%20foothills%20and%20dense%20Pacific%20Northwest%20forest%20in%20background%2C%20warm%20afternoon%20golden%20light%20illuminating%20the%20valley%2C%20clear%20blue%20sky%20with%20light%20clouds%2C%20peaceful%20university%20town%20atmosphere%2C%20photorealistic%20landscape%20photography%2C%20vibrant%20Pacific%20Northwest%20colors%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=eugene-hero-01&orientation=landscape",
  heroImageAlt: "Eugene Oregon cityscape with Willamette River and Cascade foothills",
  geoPosition: "44.0521;-123.0868",
  geoPlacename: "Eugene, Oregon",
  lastModified: "2026-05-29",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "300 5th Ave, Suite 600, Seattle, WA 98104",
    distance: "About 285 miles north — 4.5 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Eugene", zips: "97401–97408" },
    { name: "Springfield", zips: "97477–97478" },
    { name: "Cottage Grove", zips: "97424" },
    { name: "Junction City", zips: "97448" },
    { name: "Creswell", zips: "97426" },
    { name: "Coburg", zips: "97408" },
    { name: "Veneta", zips: "97487" },
    { name: "Elmira", zips: "97437" },
    { name: "Oakridge", zips: "97463" },
    { name: "Florence", zips: "97439" },
    { name: "Harrisburg", zips: "97446" },
    { name: "Monroe", zips: "97456" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home in Eugene or Springfield in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Eugene", desc: "FedEx and UPS locations in Eugene offer overnight shipping. Most clients ship same-day and documents arrive next morning." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed before submission — essential for urgent passport renewal in Eugene where there is no local regional agency." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport agency — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Ryan H.",
      location: "Eugene, OR",
      rating: 5,
      text: "I'm a graduate student at UO and needed an emergency passport for a research trip to Germany. Three weeks, expired passport. Fast Passport Center got me renewed in 7 days — I shipped from the FedEx on Valley River Way. Couldn't have done it without them.",
      service: "Passport Renewal",
    },
    {
      name: "Linda P.",
      location: "Springfield, OR",
      rating: 5,
      text: "We needed a passport for our 10-year-old before a cruise to Mexico. Being in Springfield, I had no idea where to start. Fast Passport Center walked us through the child passport process step by step — both parents signed off and we had it in 9 days.",
      service: "Child Passport",
    },
    {
      name: "Travis M.",
      location: "Cottage Grove, OR",
      rating: 5,
      text: "Lost my passport a week before a business trip to Canada. Fast Passport Center guided me through the DS-64 form, what documents to gather, and expedited the replacement in 5 days. Living in Cottage Grove I thought I was stuck. They saved the trip.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "eugene-main-post",
      name: "Eugene Main Post Office",
      type: "USPS Passport Acceptance",
      address: "520 Willamette St",
      city: "Eugene",
      state: "OR",
      zip: "97401",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Downtown Eugene",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Eugene. Appointment required — book online at usps.com. Photo services typically available.",
      icon: "ri-mail-line",
    },
    {
      id: "eugene-river-road",
      name: "Eugene River Road Post Office",
      type: "USPS Passport Acceptance",
      address: "1120 River Rd",
      city: "Eugene",
      state: "OR",
      zip: "97404",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "North Eugene",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "North Eugene passport acceptance option. Confirm appointment and photo service availability.",
      icon: "ri-mail-line",
    },
    {
      id: "springfield-post",
      name: "Springfield Post Office",
      type: "USPS Passport Acceptance",
      address: "100 Country Club Rd",
      city: "Springfield",
      state: "OR",
      zip: "97477",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Springfield — about 5 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Springfield option for Lane County East residents. Confirm appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "lane-county-clerk",
      name: "Lane County Clerk — Elections & Records",
      type: "County Passport Acceptance",
      address: "275 W 10th Ave",
      city: "Eugene",
      state: "OR",
      zip: "97401",
      phone: "541-682-4234",
      hours: "Mon–Fri 8am–5pm",
      distance: "Downtown Eugene — Lane County Courthouse",
      website: "https://www.lanecounty.org/government/county_departments/county_clerk",
      websiteLabel: "Lane County Clerk",
      notes: "County clerk option. Call ahead to confirm passport acceptance services are available and to schedule an appointment.",
      icon: "ri-government-line",
    },
    {
      id: "cottage-grove-post",
      name: "Cottage Grove Post Office",
      type: "USPS Passport Acceptance",
      address: "400 H St",
      city: "Cottage Grove",
      state: "OR",
      zip: "97424",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Cottage Grove — about 20 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Southern Lane County option. Confirm appointment and photo availability before visiting.",
      icon: "ri-mail-line",
    },
    {
      id: "junction-city-post",
      name: "Junction City Post Office",
      type: "USPS Passport Acceptance",
      address: "655 Greenwood St",
      city: "Junction City",
      state: "OR",
      zip: "97448",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Junction City — about 15 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "North Lane County option. Confirm appointment availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Eugene before international travel?",
      a: "Yes. Through Fast Passport Center, Eugene travelers can get an expedited passport in as little as 3–9 business days, and emergency same-day processing is available depending on your travel documentation. Eugene does not have a regional passport agency — Fast Passport Center is the most practical option for most Lane County travelers.",
    },
    {
      q: "How fast can I renew my passport in Eugene?",
      a: "Fast passport renewal in Eugene is available through Fast Passport Center in as little as 3 business days. Most Eugene-area adults renewing a passport can do so entirely by mail — start online, complete documents at home, and ship from any FedEx or UPS in Eugene. No office visit required for most renewals.",
    },
    {
      q: "What if my passport expires before my cruise?",
      a: "If your passport expires before your cruise departure, you need to act immediately. Cruise lines require passports that are valid for the entire duration of the voyage, and many Caribbean and Mexican cruise ports require additional validity beyond your return date. Fast Passport Center can expedite your renewal so you don't miss the ship.",
    },
    {
      q: "Can I get a same-day passport appointment near Eugene?",
      a: "The nearest regional passport agency is in Seattle — about 285 miles north. Same-day or emergency appointments at the Seattle agency require documented life-or-death emergencies and are never guaranteed. For most urgent passport situations in Eugene, Fast Passport Center is the faster, more predictable option.",
    },
    {
      q: "Can I expedite a child passport in Eugene?",
      a: "Yes, child passport expediting in Eugene is possible through Fast Passport Center. Minors under 16 require an in-person acceptance step with parental consent — both parents typically must be present or provide documentation. We provide a clear checklist and guide you through every step. Expedited processing is available after the acceptance step.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "If your passport is lost before an upcoming trip, you need to complete Form DS-64 to report it lost and apply for a replacement. Fast Passport Center guides Eugene travelers through this process and can expedite the replacement. Act immediately — a lost passport cannot be used even if found later, and replacements require extra steps.",
    },
    {
      q: "Does Eugene have a passport agency?",
      a: "Eugene, Oregon does not have a regional passport agency. The nearest options are the Seattle Passport Agency (about 285 miles north) and the San Francisco Passport Agency (about 550 miles south). Fast Passport Center eliminates the need to travel to either city — everything is handled remotely with overnight shipping from Eugene.",
    },
    {
      q: "How do University of Oregon students and staff get expedited passports?",
      a: "UO students and staff needing passports for research trips, study abroad, or international conferences can use Fast Passport Center's expedited service. Start online, ship documents from the FedEx on Valley River Way or UPS on W 11th, and most processing takes 3–9 business days. We handle everything after you ship.",
    },
    {
      q: "Can I get a passport courier service in Eugene?",
      a: "Yes. Fast Passport Center is a registered U.S. Department of State passport courier serving Eugene, Springfield, and all of Lane County. We offer courier service — reserved agency submission with predictable turnaround — without requiring you to travel to a regional passport agency.",
    },
    {
      q: "What is the fastest way to renew a passport in Eugene?",
      a: "The fastest way to renew a passport in Eugene is through Fast Passport Center's expedited courier service — processing in as little as 3 business days. Start your order online, complete your documents at home, and ship overnight from Eugene. It's faster and more predictable than going through the standard post office route.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Eugene W 11th Ave", address: "2695 W 11th Ave, Eugene, OR 97402", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Eugene Chad Dr", address: "2550 Chad Dr, Eugene, OR 97408", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Valley River Way", address: "225 Valley River Way, Eugene, OR 97401", hours: "Mon–Fri 7 AM – 9 PM, Sat–Sun 8 AM – 6 PM" },
    { name: "The UPS Store — W 11th Ave", address: "1551 W 11th Ave, Eugene, OR 97402", hours: "Mon–Fri 8 AM – 7 PM, Sat 9 AM – 5 PM" },
    { name: "Walmart Supercenter — Eugene", address: "4550 W 11th Ave, Eugene, OR 97402", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Springfield", address: "3500 Gateway St, Springfield, OR 97477", hours: "Daily 8 AM – 9 PM" },
    { name: "Costco — Eugene", address: "4821 Commercial Ave, Eugene, OR 97402", hours: "Mon–Fri 10 AM – 8:30 PM, Sat–Sun 9:30 AM – 6 PM" },
    { name: "CVS — Springfield", address: "3399 Gateway St, Springfield, OR 97477", hours: "Daily 7 AM – 10 PM" },
  ],
  dmvLocations: [
    {
      name: "Eugene DMV — Oregon Driver & Motor Vehicle Services",
      address: "3040 Delta Oaks Dr, Eugene, OR 97408",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Main Eugene DMV. Driver licenses, Oregon REAL ID, ID cards. Appointment recommended — book online at oregondmv.com.",
      featured: true,
    },
    {
      name: "Springfield DMV Office",
      address: "3030 Gateway St, Springfield, OR 97477",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Springfield / East Lane County option. Appointment recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "EUG",
      name: "Eugene Airport — Mahlon Sweet Field",
      distance: "Primary airport — 7 miles northwest of downtown",
      address: "28801 Douglas Dr, Eugene, OR 97402",
      note: "EUG offers flights to major West Coast hubs and seasonal service. International connections typically through PDX, SFO, or LAX. Valid passport required for all international departures.",
      flightUrl: "https://www.eugene-or.gov/167/Airport",
      flightLabel: "EUG Airport Website",
      featured: true,
    },
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Full international hub — about 110 miles north (2 hours)",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "Many Eugene-area travelers drive to PDX for direct international flights to Europe and Asia. Ensure your passport is valid before booking.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from EUG or PDX in 24–72 hours? Emergency passport processing for Eugene and Lane County travelers — as fast as 24 hours.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast passport renewal for Eugene residents — no office visit required for most adult renewals. Ship from Eugene and we handle the rest.",
      bullets: ["Eligible for expedited processing", "Ship from FedEx or UPS in Eugene", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a cruise or international trip? We guide Eugene travelers through DS-64 and fast replacement.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Urgent child passport for Eugene families — clear guidance on parental consent and the in-person acceptance step.",
      bullets: ["Parental consent guidance included", "Checklist provided", "Expedited after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport for Eugene residents — we guide you through the acceptance clerk visit and handle the expedited submission.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered State Dept. courier serving Eugene, Springfield, and Lane County — same-day passport help without the Seattle drive.",
      bullets: ["Registered State Dept. courier", "Reserved daily slots", "Not affected by shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Eugene, Oregon travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport services for Eugene, OR residents needing faster than standard government timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Eugene residents — expedited processing as fast as 3 business days." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Eugene, OR travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Eugene, Oregon families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier service for passport processing, serving Eugene and Lane County, Oregon." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long", description: "Eugene travelers often underestimate how long standard processing takes — it's currently 6–13 weeks or more during peak season. If your Europe trip or cruise is within 10 weeks, you need expedited service now." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photo rejection is the most common delay. Walgreens and CVS in Eugene usually do fine, but always ask the tech to verify spec compliance before you leave." },
    { icon: "ri-file-damage-line", title: "Form Errors", description: "A wrong date or missing signature sends your application back — costing weeks. Our checklist reviews everything before submission." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "You need a certified copy with a raised seal, not a hospital record or photocopy. We tell you exactly what you need for your specific situation." },
    { icon: "ri-calendar-close-line", title: "Assuming USPS is Fast Enough", description: "Standard processing at the Eugene post office runs 6–13 weeks. If you're flying in under 10 weeks, use expedited courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatches", description: "Passport must match ticket exactly — middle names, suffixes, and hyphens matter for international flights. We flag these before submission." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that matches your Eugene travel date. Complete your secure online order in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Get Your Checklist", description: "We confirm your situation and send a personalized document checklist — exactly what you need." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Overnight from Eugene", description: "Drop at FedEx on Valley River Way or UPS on W 11th Ave. Documents arrive at our processing center next morning." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through reserved agency slots — no public appointment wait, no Seattle drive required." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Ready", description: "Your passport is completed within your selected timeframe and shipped directly back to you in Eugene." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited, often weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Drive 285 miles to Seattle or 550 miles to San Francisco", fpc: "Ship documents overnight from Eugene" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Emergency passport services in Portland", route: "/cities/portland" },
    { name: "Fast passport renewal in Salem", route: "/cities/salem" },
    { name: "Expedited passport help in Springfield", route: "#" },
    { name: "Rush passport service for Willamette Valley travelers", route: "/states/oregon" },
    { name: "Passport agency near Corvallis", route: "#" },
  ],
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping label. Ship your documents from FedEx on Valley River Way or UPS on W 11th Ave in Eugene. Documents arrive at our processing center next morning — no travel to Portland or Seattle required.",
  fpcOfficeDistance: "Serving Eugene, Springfield, and all of Lane County remotely",
  serviceAreaIntro: "Whether you are in Eugene, Springfield, Cottage Grove, Junction City, Florence, or anywhere in Lane County, Fast Passport Center makes expedited passport processing simple — start online, ship overnight from Eugene, and we handle everything.",
  nearbyCitiesIntro: "We serve all Lane County zip codes — and beyond. Ship your documents overnight from Eugene and your application is at our processing center by next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Eugene as simple as possible — most clients never visit an office.",
  mistakesIntro: "These are the most common mistakes Eugene and Lane County travelers make that cause passport delays. Fast Passport Center reviews everything before submission.",
  testimonialsIntro: "Real reviews from Eugene, Springfield, and Cottage Grove travelers who needed their passports fast and trusted Fast Passport Center to get it done.",
  faqIntro: "The most common questions Eugene and Lane County travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
};